import { motion } from 'framer-motion';
import { useState, ReactNode } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Shield, Brain, Lock, Check, Coins, Signal, MessageSquare, Mic, Globe, ArrowRight, ArrowLeft, Cpu, Download, Zap, Github, ExternalLink, Code2 } from 'lucide-react';
import StripeCheckoutModal from './StripeCheckoutModal';
import LifestyleCarousel from './LifestyleCarousel';
import PhoneAvailabilityCounter from './PhoneAvailabilityCounter';
import BatchAvailabilityCounter from './BatchAvailabilityCounter';
import ShippingBatchSchedule from './ShippingBatchSchedule';
import { usePhoneInventory } from '../../hooks/usePhoneInventory';
import { SELF_PHONE_PRICE_LABEL } from '../../lib/selfPhoneProduct';
import {
  SELF_OS_GITHUB_URL,
  SELFOS_BETA,
  SELFOS_DOWNLOAD_SELF_PHONE,
} from '../../lib/selfOsDownload';
import AeoPageHead from '../AeoPageHead';
import { SELF_PHONE_PAGE_FAQS } from '../../lib/aeo/faqs';
import { buildSelfPhoneProductJsonLd } from '../../lib/aeo/jsonLd';

const DEVELOPER_HIGHLIGHTS = [
  {
    icon: Code2,
    title: 'Agents & SELF Apps',
    text: 'Build on-device agents and installable SELF Apps with the open agent-sdk and app-sdk — extend what runs on SELF Phone and SELF OS.',
  },
  {
    icon: Cpu,
    title: 'Your models, your stack',
    text: 'Wire Resonatia, OpenRouter, OpenAI, Anthropic, Gemini, or local runtimes. Community Edition ships example agents; you bring the integrations.',
  },
  {
    icon: Github,
    title: 'Android today, iOS soon',
    text: 'Clone the repo, run the self-shell app on Android now, and follow the same personal-intelligence vision as the full SELF OS platform.',
  },
];

interface TooltipProps {
  text: string;
  isVisible: boolean;
}

interface IconStepProps {
  icon: ReactNode;
  label: string;
  tooltipText: string;
}

const Tooltip = ({ text, isVisible }: TooltipProps) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 10 }}
    transition={{ duration: 0.2 }}
    className="absolute -top-16 left-1/2 z-50 w-48 -translate-x-1/2 rounded-lg border border-white/15 bg-black/90 px-4 py-2 backdrop-blur-sm"
  >
    <div className="text-center text-xs text-white/70">{text}</div>
    <div className="absolute bottom-[-6px] left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r border-white/15 bg-black/90"></div>
  </motion.div>
);

const IconStep = ({ icon, label, tooltipText }: IconStepProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);

  const handleInteraction = () => {
    if (tooltipTimer) clearTimeout(tooltipTimer);
    
    setShowTooltip(true);
    
    const timer = setTimeout(() => setShowTooltip(false), 3000);
    setTooltipTimer(timer);
  };

  return (
    <motion.div 
      className="relative flex flex-col items-center text-center"
      whileHover={{ scale: 1.05 }}
      onHoverStart={handleInteraction}
      onHoverEnd={() => setShowTooltip(false)}
      onTapStart={handleInteraction}
    >
      <Tooltip text={tooltipText} isVisible={showTooltip} />
      <div className="mb-4 flex h-20 w-20 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm">
        <span className="text-4xl">{icon}</span>
      </div>
      <p className="text-sm text-white/65">{label}</p>
    </motion.div>
  );
};

const JourneyFlow = () => {
  const steps = [
    {
      icon: "📥",
      label: "Download Models",
      tooltipText: "Browse Resonatia and download edge-optimized models to your SELF Phone or SELF OS device."
    },
    {
      icon: "🔗",
      label: "Sync to the Mesh",
      tooltipText: "Models sync to the Resonatia network — your device becomes an active mesh node."
    },
    {
      icon: "⚡",
      label: "Serve API Requests",
      tooltipText: "When developers call the Resonatia API, your node can help process inference on the network."
    },
    {
      icon: "🪙",
      label: "Earn ESSENCE",
      tooltipText: "You earn ESSENCE rewards every time your node contributes to a Resonatia API request."
    }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="flex justify-between items-center"
    >
      {steps.map((step, index) => (
        <div key={`step-container-${index}`} className="flex items-center">
          <IconStep
            icon={step.icon}
            label={step.label}
            tooltipText={step.tooltipText}
          />
          {index < steps.length - 1 && (
            <motion.div 
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: index * 0.2 }}
              className="h-px w-16 bg-white/20"
            />
          )}
        </div>
      ))}
    </motion.div>
  );
};

const SelfPhonePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { inventory, loading: inventoryLoading } = usePhoneInventory();
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [isFlashKit, setIsFlashKit] = useState(false);
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

  const handlePreOrder = () => {
    if (inventory.soldOut) return;
    setIsFlashKit(false);
    setShowStripeModal(true);
  };

  return (
    <>
      <Helmet>
        <title>SELF Phone — Resonatia Mesh Node | SELF Labs</title>
        <meta name="description" content="SELF Phone is your node on the Resonatia mesh. Download models from Resonatia, sync to the network, and earn ESSENCE when API requests are processed through your device. SELF OS users can run nodes too." />
        <meta property="og:title" content="SELF Phone — Your Resonatia Mesh Node" />
        <meta property="og:description" content="Download Resonatia models, sync to the mesh, and earn ESSENCE when your node helps process API requests. Available on SELF Phone and SELF OS." />
        <meta property="og:image" content="/assets/images/Phone%20in%20box.png" />
        <meta property="og:url" content="https://selflabs.xyz/self-phone" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SELF Phone — Your Resonatia Mesh Node" />
        <meta name="twitter:description" content="Download Resonatia models, sync to the mesh, and earn ESSENCE when your node helps process API requests." />
        <meta name="twitter:image" content="/assets/images/Phone%20in%20box.png" />
        <meta name="keywords" content="SELF Phone, Resonatia, mesh node, AI API, model hosting, ESSENCE rewards, SELF OS, satellite phone, SELF Labs" />
      </Helmet>
      <AeoPageHead
        path="/self-phone"
        faqs={SELF_PHONE_PAGE_FAQS}
        extraJsonLd={[buildSelfPhoneProductJsonLd()]}
      />

      <div
        id="self-phone"
        data-avatar-target="self-phone"
        className="min-h-screen bg-black text-white"
      >
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/products"
                className="group inline-flex items-center text-sm text-white/50 transition-colors hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                Back to Products
              </Link>
            </motion.div>

            {checkoutCancelled && (
              <p className="mt-6 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-sm text-white/60">
                Checkout was cancelled. {inventory.remaining} phones still available when you&apos;re ready.
              </p>
            )}

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-20">
        <div className="relative z-10">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Phone className="h-3.5 w-3.5" />
                Resonatia mesh node
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                Your Voice. Your AI. Your Signal.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-white/65 sm:text-xl">
                SELF Phone is the node for{' '}
                <Link to="/resonatia" className="text-white underline underline-offset-2 hover:text-white/90">
                  Resonatia
                </Link>
                — download models, sync them to the mesh, and earn ESSENCE when your device helps process API
                requests. SELF OS users can run as nodes too.
              </p>
              <PhoneAvailabilityCounter inventory={inventory} loading={inventoryLoading} />
              <BatchAvailabilityCounter inventory={inventory} loading={inventoryLoading} />
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <motion.button
                  whileHover={inventory.soldOut ? undefined : { scale: 1.02 }}
                  whileTap={inventory.soldOut ? undefined : { scale: 0.98 }}
                  onClick={handlePreOrder}
                  disabled={inventory.soldOut || inventoryLoading}
                  className="rounded-xl bg-white px-8 py-4 text-lg font-semibold text-black transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/30 disabled:text-white/50"
                >
                  {inventory.soldOut ? 'Sold Out' : `Buy Now ${SELF_PHONE_PRICE_LABEL}`}
                </motion.button>
                <a href={SELFOS_DOWNLOAD_SELF_PHONE}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10 sm:w-auto"
                  >
                    Download SELF OS beta
                  </motion.button>
                </a>
              </div>
              <p className="mt-3 text-sm text-white/45">
                Android 9+ · sideload · public beta v{SELFOS_BETA.version}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex flex-col items-center justify-center"
            >
              <motion.img
                src="/assets/images/Untitled design (1).svg"
                alt="SELF Phone Preview"
                className="relative w-full max-w-[400px] rounded-3xl border border-white/10"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
              <p className="mt-4 text-center text-xs italic text-white/40">
                *Actual device appearance may vary from images shown
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resonatia Node */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
              <Zap className="h-3.5 w-3.5" />
              Resonatia × SELF Phone
            </span>
            <h2 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">
              Your device is a Resonatia node
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-white/65">
              Resonatia is our global AI API — hundreds of edge-optimized models through one unified endpoint.
              SELF Phone and SELF OS turn your hardware into mesh infrastructure: download models, keep them in
              sync, and get paid when the network routes API traffic through you.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Download,
                title: 'Download models',
                text: 'Pull edge-ready models from Resonatia onto your SELF Phone or SELF OS device.',
              },
              {
                icon: Cpu,
                title: 'Sync to the mesh',
                text: 'Models register on the Resonatia network so your device becomes an active inference node.',
              },
              {
                icon: Zap,
                title: 'Process API calls',
                text: 'When someone hits the Resonatia API, requests can be served by nodes like yours on the mesh.',
              },
              {
                icon: Coins,
                title: 'Earn ESSENCE',
                text: 'Your node earns ESSENCE rewards for every API request it helps process.',
              },
            ].map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center"
              >
                <step.icon className="mx-auto h-8 w-8 text-white/80" />
                <h3 className="mt-4 font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{step.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mx-auto mt-10 flex max-w-xl flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              to="/resonatia"
              className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
            >
              Learn about Resonatia
            </Link>
            <a
              href="https://resonatia.io/models"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10"
            >
              Browse models on resonatia.io
            </a>
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-white/45">
            No SELF Phone yet?{' '}
            <a
              href={SELFOS_DOWNLOAD_SELF_PHONE}
              className="text-white/70 underline underline-offset-2 hover:text-white"
            >
              Download Self OS beta
            </a>{' '}
            for Android and join the mesh as a node.
          </p>
        </div>
      </section>

      {/* Open source */}
      <section id="open-source" className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
              <Github className="h-3.5 w-3.5" />
              Open source · AGPL-3.0
            </span>
            <h2 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">Open source</h2>
            <p className="mt-4 text-lg leading-relaxed text-white/65">
              SELF OS Personal Intelligence is open source — an Android shell for building your own
              agent-powered environment with apps, workflows, and local intelligence. Clone the repo to
              build on the same stack that powers SELF Phone, or fork it and ship your own applications.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-3">
            {DEVELOPER_HIGHLIGHTS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              >
                <item.icon className="h-8 w-8 text-white/80" />
                <h3 className="mt-4 font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center sm:p-8">
            <p className="text-sm leading-relaxed text-white/55">
              The community edition includes a Material 3 shell, <code className="text-white/70">AgentRuntime</code>,
              mock Harmony Mesh previews, and example agents — with clear boundaries between the open layer and
              managed SELF OS Core (ESSENCE, Aura monetization, telecom, and cloud orchestration).
            </p>
            <a
              href={SELF_OS_GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
            >
              <Github className="h-4 w-4" />
              View SELF-OS on GitHub
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">What You Get</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <Cpu className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Resonatia Mesh Node</h3>
              <p className="text-white/50 mb-4">Download models from Resonatia, sync them to the network, and earn ESSENCE when your node helps process API requests. Built into SELF Phone and SELF OS.</p>
              <Link to="/resonatia" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <Globe className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Free Global Signal</h3>
              <p className="text-white/50 mb-4">Communicate worldwide without carrier plans. Powered by satellite providers like Starlink and others (expanding). Full independence, anywhere on Earth.</p>
              <Link to="/features/global-signal" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <Brain className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Role Model AI</h3>
              <p className="text-white/50 mb-4">Your native AI App Store. Install AI apps, agents, and tools in one place. Harmony can access and operate apps for you by voice or signal. Early access to new apps included.</p>
              <Link to="/features/role-model-ai" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <Mic className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Personal AI Voice</h3>
              <p className="text-white/50 mb-4">Create your own Harmony Agent trained in your voice and style. Speak through it, command with it, connect through it. Your signal, amplified.</p>
              <Link to="/features/personal-ai-voice" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <MessageSquare className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Agent Communication</h3>
              <p className="text-white/50 mb-4">Instant agent-to-agent and user-to-user walkie-talkie communication. Encrypted, carrier-free, and built for real-time execution. Push. Talk. Execute.</p>
              <Link to="/features/agent-communication" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <Shield className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Aura Shield</h3>
              <p className="text-white/50 mb-4">Always-on data encryption and protection. Device-level security from day one. Full data monetization control unlocks in Phase 2.</p>
              <Link to="/features/aura-shield" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <Coins className="w-12 h-12 text-white/80 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Private Earnings</h3>
              <p className="text-white/50 mb-4">Earn ESSENCE when your Resonatia node processes API requests, plus rewards from Harmony task signals and optional encrypted signal monetization.</p>
              <Link to="/signal-engine" className="text-white/80 hover:text-white transition-colors inline-flex items-center">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SELF Phone Specs */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-8 text-4xl font-bold tracking-tight text-center">
            SELF Phone Specs
          </h2>
          <p className="text-xl text-white/65 text-center mb-16">
            Built for Power. Designed for Privacy. Connected to Signal.
          </p>
          
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative flex justify-center items-center mb-16"
            >
              <motion.img
                src="/assets/images/SELF PHONE2.svg"
                alt="SELF Phone Specifications"
                className="relative w-full max-w-[800px] rounded-3xl border border-white/10"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </motion.div>
            <p className="text-sm text-white/50 text-center mb-16 italic">
              *Actual device appearance may vary from images shown
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Operating System</h3>
                <p className="text-white/65">SELF OS (Native Signal-Based Operating System)</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Processor</h3>
                <p className="text-white/65">MTK6765 Octa-Core 2.35GHz + 1.7GHz</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Memory</h3>
                <p className="text-white/65">4GB DDR4 RAM</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Storage</h3>
                <p className="text-white/65">64GB standard (expandable)</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Screen</h3>
                <p className="text-white/65">6.3" HD+ Touchscreen</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Battery</h3>
                <p className="text-white/65">5000mAh (48+ hour battery life)</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Camera</h3>
                <p className="text-white/65">Front: 5MP</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Connectivity</h3>
                <p className="text-white/65">Global Satellite, Wi-Fi 6, Bluetooth 5.2, Dual SIM (Micro SIM)</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Interface</h3>
                <p className="text-white/65">USB-C 2.0 OTG Port</p>
              </div>
              <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
                <h3 className="text-xl font-semibold mb-2 text-white/80">Weight & Durability</h3>
                <p className="text-white/65">0.32 kg, IP68 Dust/Water Resistant (ATEX 4400mAh battery optional)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside the Box */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-8 text-4xl font-bold tracking-tight text-center">
            What's Inside Your SELF Box
          </h2>
          
            <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative flex justify-center items-center mb-16"
          >
            <motion.img
              src="/assets/images/SELF.svg"
              alt="SELF Box Contents"
              className="relative w-full max-w-[800px] rounded-3xl border border-white/10"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            />
            </motion.div>
          <p className="text-sm text-white/50 text-center mb-16 italic">
            *Actual device appearance may vary from images shown
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-white/80">SELF Phone</h3>
              <p className="text-white/65">1x SELF Phone (First Wave Edition)</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-white/80">Charger</h3>
              <p className="text-white/65">1x USB-C Fast Charger</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-white/80">Documentation</h3>
              <p className="text-white/65">1x Technical Specs Manual and Setup Guide</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-white/80">Onboarding Kit</h3>
              <p className="text-white/65">1x Signal Onboarding Kit</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-white/80">Software</h3>
              <p className="text-white/65">Pre-Activated Aura Shield and SELF OS</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section className="relative overflow-hidden border-t border-white/10 pt-20">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.img
                  src="/assets/images/Untitled design (2).svg"
                alt="SELF Phone Features"
                className="relative w-full rounded-3xl border border-white/10"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
                <p className="text-sm text-white/50 text-center mt-4 italic">
                  *Actual device appearance may vary from images shown
                </p>
            </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
                Designed for Your Digital Life
              </h2>
              
              <div className="space-y-6">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/[0.03] p-6 rounded-2xl border border-white/10"
                >
                  <h3 className="text-xl font-semibold mb-2 text-white/80">Seamless Integration</h3>
                  <p className="text-white/65">Experience fluid interaction between your AI agents and daily tasks, all through an intuitive interface.</p>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/[0.03] p-6 rounded-2xl border border-white/10"
                >
                  <h3 className="text-xl font-semibold mb-2 text-white/80">Satellite Connectivity</h3>
                  <p className="text-white/65">Stay connected anywhere with direct satellite communication, no traditional carrier required.</p>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/[0.03] p-6 rounded-2xl border border-white/10"
                >
                  <h3 className="text-xl font-semibold mb-2 text-white/80">Privacy First</h3>
                  <p className="text-white/65">Built-in Aura Shield ensures your data remains protected and under your control at all times.</p>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/[0.03] p-6 rounded-2xl border border-white/10"
                >
                  <h3 className="text-xl font-semibold mb-2 text-white/80">Earn As You Serve</h3>
                  <p className="text-white/65">Run Resonatia models on your node and earn ESSENCE every time the API routes inference through your device.</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Signal Economy Portal Section */}
      <section className="relative overflow-hidden border-t border-white/10 pt-20">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Resonatia powers the reward loop
            </h2>
            <p className="text-xl md:text-2xl text-white/65 max-w-4xl mx-auto leading-relaxed">
              Developers call the Resonatia API. Your SELF Phone or SELF OS device syncs models to the mesh and
              earns ESSENCE when it helps process those requests — alongside satellite signal, AI agents, and Aura
              Shield protection.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {/* Global Signal Network */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="bg-white/[0.03] p-8 rounded-xl border border-white/10"
            >
              <div className="flex justify-center mb-6">
                <Globe className="w-16 h-16 text-white/80" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Global Signal Network</h3>
              <p className="text-white/65 text-center">
                Communicate worldwide without carriers — powered by satellite providers and decentralized infrastructure.
              </p>
            </motion.div>

            {/* Harmony Agent Labor */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="bg-white/[0.03] p-8 rounded-xl border border-white/10"
            >
              <div className="flex justify-center mb-6">
                <Zap className="w-16 h-16 text-white/80" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Resonatia API Traffic</h3>
              <p className="text-white/65 text-center">
                Incoming API calls from Resonatia can be routed to mesh nodes like your SELF Phone or SELF OS device.
              </p>
            </motion.div>

            {/* ESSENCE Rewards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="bg-white/[0.03] p-8 rounded-xl border border-white/10"
            >
              <div className="flex justify-center mb-6">
                <Coins className="w-16 h-16 text-white/80" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Node ESSENCE Rewards</h3>
              <p className="text-white/65 text-center">
                Every API request your node helps process earns ESSENCE — cash out, spend, or reinvest in the network.
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              className="rounded-xl bg-white px-8 py-4 text-lg font-semibold text-black transition-colors hover:bg-white/90"
              onClick={() => navigate('/resonatia')}
            >
              Explore Resonatia →
            </motion.button>
          </div>
        </div>
      </section>

      {/* How the SELF Phone Rewards You */}
      <section className="relative overflow-hidden border-t border-white/10 pt-20">
        <div className="container mx-auto px-4 relative">
          {/* Main Title */}
          <h2 className="text-5xl font-bold tracking-tight text-center">
            How your node earns on Resonatia
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-center text-white/55">
            From model download to ESSENCE payout — the loop runs on your SELF Phone or SELF OS device.
          </p>

          {/* Horizontal Journey Flow */}
          <div className="max-w-6xl mx-auto mb-24">
            <JourneyFlow />
          </div>

          {/* What You Can Do with ESSENCE */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h3 className="text-3xl font-semibold mb-12 text-center">
              What You Can Do with ESSENCE
            </h3>

            <div className="flex flex-wrap justify-center gap-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-8 py-4 transition-colors hover:border-white/20"
              >
                <span className="text-2xl mb-2 block">💵</span>
                <span className="text-white/65">Convert to Cash</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-8 py-4 transition-colors hover:border-white/20"
              >
                <span className="text-2xl mb-2 block">🛒</span>
                <span className="text-white/65">Spend in Role Model AI Store</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-8 py-4 transition-colors hover:border-white/20"
              >
                <span className="text-2xl mb-2 block">🔄</span>
                <span className="text-white/65">Hold for Upgrades</span>
              </motion.button>
            </div>
          </div>

          {/* Passive Earnings Note */}
          <p className="text-center text-white/50 italic text-sm max-w-2xl mx-auto">
            Keep models synced and online — your node earns automatically when Resonatia routes API traffic through it.
          </p>
        </div>
      </section>

      {/* How We Protect Your Signal */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              How We Protect Your Signal
            </h2>
            <p className="text-xl text-white/50">
              Your signal is your sovereignty. We protect it at every level.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* First Row */}
            <div className="bg-white/[0.03] p-8 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-white/80" />
                <h3 className="text-2xl font-semibold text-white/80">Zero-Knowledge Encryption</h3>
              </div>
              <div className="space-y-3 text-white/65">
                <p>Zero-knowledge encryption at the hardware and live signal layers.</p>
                <p>Ensures no external entity can see or alter tasks or transmissions.</p>
              </div>
            </div>

            <div className="bg-white/[0.03] p-8 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-white/80" />
                <h3 className="text-2xl font-semibold text-white/80">Aura Shield Protection</h3>
              </div>
              <ul className="space-y-3 text-white/65">
                <li>• Always-on personal data protection</li>
                <li>• No monthly fees, no surveillance</li>
                <li>• Full user-controlled data sharing and monetization</li>
              </ul>
            </div>

            {/* Second Row */}
            <div className="bg-white/[0.03] p-8 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Signal className="w-6 h-6 text-white/80" />
                <h3 className="text-2xl font-semibold text-white/80">Private Signal Routing</h3>
              </div>
              <div className="space-y-3 text-white/65">
                <p>Secure, decentralized mesh routing.</p>
                <p>No centralized control points or vulnerabilities.</p>
              </div>
            </div>

            <div className="bg-white/[0.03] p-8 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-white/80" />
                <h3 className="text-2xl font-semibold text-white/80">Signal Vault Security</h3>
              </div>
              <ul className="space-y-3 text-white/65">
                <li>• Private encrypted storage for task history and agent activity</li>
                <li>• Secure decentralized backups across the Signal Mesh</li>
              </ul>
            </div>

            {/* Third Row */}
            <div className="bg-white/[0.03] p-8 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-white/80 text-xl">⛓️</div>
                <h3 className="text-2xl font-semibold text-white/80">Immutable Data Fabric</h3>
              </div>
              <div className="space-y-3 text-white/65">
                <p>Data recorded immutably using blockchain-inspired methods.</p>
                <p>Enables future-proof compliance, auditability, and protection against data tampering.</p>
              </div>
            </div>

            <div className="bg-white/[0.03] p-8 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-white/80 text-xl">🔐</div>
                <h3 className="text-2xl font-semibold text-white/80">Attribute-Based Access Control</h3>
              </div>
              <div className="space-y-3 text-white/65">
                <p>Fine-grained access permissions tied to user roles and attributes.</p>
                <p>Only authorized personnel or agents can view or interact with protected data.</p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mt-16 text-center">
            <p className="text-white/65 leading-relaxed">
              SELF Phone is built for full-spectrum sovereignty. Your signals are private, your actions are encrypted,
              and your data stays under your control — across communications, transactions, and AI operations.
            </p>
          </div>
        </div>
      </section>

      {/* Imagine This... Lifestyle Carousel */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-center mb-6">
              Imagine This...
            </h2>
            <p className="text-xl md:text-2xl text-white/65 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              Real-life moments made effortless, protected, and more rewarding with your SELF Phone.
            </p>
          </motion.div>
          
          <div className="relative">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-white/10" />
            <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-black to-transparent z-10" />
            <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-black to-transparent z-10" />
            
            <LifestyleCarousel />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="text-3xl font-bold text-white/80">
              This isn't sci-fi. It's SELF.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pre-Order Section */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Choose Your Path</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* SELF Phone Option */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <h3 className="text-3xl font-bold mb-6">Get the SELF Phone</h3>
              <p className="text-4xl font-bold text-white/80 mb-2">{SELF_PHONE_PRICE_LABEL}</p>
              <PhoneAvailabilityCounter inventory={inventory} loading={inventoryLoading} />
              <BatchAvailabilityCounter inventory={inventory} loading={inventoryLoading} />
              <ul className="space-y-4 mb-8 mt-6">
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>SELF Phone with free global satellite signal</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>1 Year Aura Shield</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>Custom Resonance Skin</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>Role Model AI Access</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>Resonatia mesh node — download models and earn on API traffic</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>Signal Governance Access</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-white/80 mr-2" />
                  <span>Early claim to private reward balance</span>
                </li>
              </ul>
              <motion.button
                whileHover={inventory.soldOut ? undefined : { scale: 1.05 }}
                whileTap={inventory.soldOut ? undefined : { scale: 0.95 }}
                onClick={handlePreOrder}
                disabled={inventory.soldOut || inventoryLoading}
                className="w-full rounded-xl bg-white px-8 py-4 text-lg font-semibold text-black transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/30 disabled:text-white/50"
              >
                {inventory.soldOut ? 'Sold Out' : `Buy Now ${SELF_PHONE_PRICE_LABEL}`}
              </motion.button>
            </motion.div>

            {/* Flash Option */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white/[0.03] p-8 rounded-2xl border border-white/10"
            >
              <h3 className="text-3xl font-bold mb-6 flex items-center gap-2">📱 Already Have an Android Device?</h3>
              <p className="text-xl text-white/65 mb-4">Download Self OS beta. Earn Essence. Convert Talks.</p>
              <p className="text-white/50 mb-4">
                You don&apos;t need new hardware to join the Resonatia mesh. Sideload the Android beta, pull models from
                resonatia.io when available, sync to the network, and earn Essence — plus Concierge Living Bio Talk
                conversion when you opt in.
              </p>
              <p className="text-sm text-white/45 mb-8">
                Android 9+ · sideload required · public beta — prototype; Essence is not cash; compute may use battery
                if you opt in.
              </p>
              <a href={SELFOS_DOWNLOAD_SELF_PHONE}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10 flex items-center justify-center gap-2"
                >
                  <Download className="h-5 w-5" /> Download Self OS beta (Android APK)
                </motion.button>
              </a>
              <p className="mt-4 text-center text-sm text-white/45">
                Developers:{' '}
                <a
                  href="#open-source"
                  className="text-white/70 underline underline-offset-2 hover:text-white"
                >
                  open source on GitHub
                </a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-2xl md:text-3xl font-light italic text-white/65">
              "This is more than a smartphone. It's a signal-powered extension of who you are."
            </blockquote>
            <div className="mt-4">
              <p className="text-white/80 font-semibold">— Milan Cheeks</p>
              <p className="text-white/50 text-sm">Root, Founder, CEO of SELF Labs Inc</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping and Delivery */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Shipping in batches</h2>
            <p className="mt-4 text-lg leading-relaxed text-white/65">
              SELF Phone ships in five limited batches — 10 phones per batch. Your batch is assigned
              at checkout based on real-time availability, and we&apos;ll tell you which batch
              you&apos;re in after you order.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-2xl">
            <BatchAvailabilityCounter inventory={inventory} loading={inventoryLoading} />
          </div>

          <div className="mx-auto mt-10 max-w-2xl">
            <ShippingBatchSchedule inventory={inventory} loading={inventoryLoading} />
          </div>

          <div className="mx-auto mt-10 grid max-w-4xl gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <h3 className="mb-4 text-xl font-semibold text-white/80">How batches work</h3>
              <ul className="space-y-2 text-white/65">
                <li>• 50 phones total across 5 batches (10 per batch)</li>
                <li>• When a batch fills, the next order moves to the following ship date</li>
                <li>• Your confirmation email includes your batch number and ship window</li>
                <li>• Carrier: secured express delivery with tracking</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <h3 className="mb-4 text-xl font-semibold text-white/80">Order protection</h3>
              <p className="text-white/65">
                Your order is protected by the SELF Labs Secure Order Guarantee. Batch assignment
                is locked in at purchase — you always know which wave you&apos;re in before your
                phone ships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-8 text-4xl font-bold tracking-tight text-center">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">Is this just another Android phone?</h3>
              <p className="text-white/65">No. SELF OS is our native operating system optimized for private signal communication and AI agent execution — it is not based on Android.</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">What is the Resonatia node on SELF Phone?</h3>
              <p className="text-white/65">SELF Phone is built to run as a Resonatia mesh node. You download edge-optimized models from Resonatia, sync them to the network, and earn ESSENCE when your device helps process API requests routed through the mesh. SELF OS on Android works the same way.</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">How do I earn rewards?</h3>
              <p className="text-white/65">Sync Resonatia models to your device and keep your node online. When developers call the Resonatia API, requests can be processed by mesh nodes like yours — and you earn ESSENCE for every contribution. Harmony task signals and other Signal Economy rewards still apply.</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">Is my data safe?</h3>
              <p className="text-white/65">Yes. Aura Shield locks your data automatically. Only you control whether anything is ever shared.</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">Can I use my own SIM card?</h3>
              <p className="text-white/65">Yes. The SELF Phone supports two Micro SIM cards for optional carrier connectivity — but global satellite messaging works without any SIM.</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">Why only 50 phones?</h3>
              <p className="text-white/65">The first SELF Phone wave is limited to 50 units. Availability updates in real time on this page as orders complete — when they&apos;re gone, checkout closes automatically.</p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">When will I receive my phone?</h3>
              <p className="text-white/65">
                Your ship date depends on your batch. We&apos;re currently filling Batch 1 (ships July
                31, 2026). When Batch 1 sells out, new orders move to Batch 2 (September 30, 2026),
                then October 17, 2026, January 17, 2027, and the final batch on March 30, 2027. Your
                batch is confirmed at checkout and in your order email.
              </p>
            </div>
            <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/10">
              <h3 className="text-xl font-semibold mb-2 text-white/80">Is ESSENCE real money or just credits?</h3>
              <p className="text-white/65">ESSENCE is a real, stable-value token. You can convert ESSENCE into cash or use it to purchase services and AI agents inside the Role Model AI app store.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="border-t border-white/10 pt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-12">
              {/* Company Info */}
              <div>
                <h3 className="text-xl font-semibold mb-6 text-white/80">SELF Labs Inc.</h3>
                <p className="text-white/50 mb-4">Powering the future of sovereign communication and AI execution.</p>
                <div className="flex space-x-4">
                  <a 
                    href="https://x.com/selflabsinc" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-white/50 hover:text-white/80 transition-colors"
                  >
                    Twitter
                  </a>
                  <a 
                    href="https://instagram.com/selflabsinc" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-white/50 hover:text-white/80 transition-colors"
                  >
                    Instagram
                  </a>
                  <a 
                    href="https://t.me/+2uJEHPAt4204M2Zh" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-white/50 hover:text-white/80 transition-colors"
                  >
                    Telegram
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-xl font-semibold mb-6 text-white/80">Quick Links</h3>
                <div className="space-y-3">
                  <Link to="/signal-engine" className="block text-white/50 hover:text-white/80 transition-colors">
                    Signal Engine
                  </Link>
                  <Link to="/harmony-litepaper" className="block text-white/50 hover:text-white/80 transition-colors">
                    Harmony Litepaper
                  </Link>
                  <Link to="/flash" className="block text-white/50 hover:text-white/80 transition-colors">
                    Flash Kit
                  </Link>
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="text-xl font-semibold mb-6 text-white/80">Features</h3>
                <div className="space-y-3">
                  <Link to="/features/global-signal" className="block text-white/50 hover:text-white/80 transition-colors">
                    Global Signal
                  </Link>
                  <Link to="/features/role-model-ai" className="block text-white/50 hover:text-white/80 transition-colors">
                    Role Model AI
                  </Link>
                  <Link to="/features/aura-shield" className="block text-white/50 hover:text-white/80 transition-colors">
                    Aura Shield
                  </Link>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-xl font-semibold mb-6 text-white/80">Legal</h3>
                <div className="space-y-3">
                  <Link to="/terms" className="block text-white/50 hover:text-white/80 transition-colors">
                    Terms & Conditions
                  </Link>
                  <Link to="/privacy" className="block text-white/50 hover:text-white/80 transition-colors">
                    Privacy Policy
                  </Link>
                  <Link to="/reseller-agreement" className="block text-white/50 hover:text-white/80 transition-colors">
                    Reseller Agreement
                  </Link>
                  <Link to="/mesh-license" className="block text-white/50 hover:text-white/80 transition-colors">
                    Mesh License
                  </Link>
                  <Link to="/developer-agreement" className="block text-white/50 hover:text-white/80 transition-colors">
                    Developer Agreement
                  </Link>
                  <Link to="/signal-mesh-fees" className="block text-white/50 hover:text-white/80 transition-colors">
                    Signal Mesh Fees
                  </Link>
                  <a 
                    href="mailto:support@rolemodel.ai" 
                    className="block text-white/50 hover:text-white/80 transition-colors"
                  >
                    Contact Support
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="mt-16 pt-8 border-t border-white/10">
              <div className="flex flex-col md:flex-row justify-center md:justify-between items-center space-y-4 md:space-y-0">
                <div className="text-white/50 text-sm text-center md:text-left">
                  &copy; {new Date().getFullYear()} SELF Labs Inc. & SELF Labs DAO L.L.C. All rights reserved.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stripe Checkout Modal */}
      <StripeCheckoutModal
        isOpen={showStripeModal}
        onClose={() => setShowStripeModal(false)}
        isFlashKit={isFlashKit}
        soldOut={inventory.soldOut}
      />
          </div>
        </div>
      </div>
    </>
  );
};

export default SelfPhonePage; 